import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'

interface WebSocketMessage {
  type: string
  data?: any
  timestamp?: string
  client_id?: string
}

export const useConnectionStore = defineStore('connection', () => {
  const toast = useToast()
  
  // State
  const socket = ref<WebSocket | null>(null)
  const isConnected = ref(false)
  const reconnectAttempts = ref(0)
  const maxReconnectAttempts = 5
  const reconnectDelay = ref(1000) // Start with 1 second
  const lastMessage = ref<WebSocketMessage | null>(null)
  const messageHistory = ref<WebSocketMessage[]>([])
  
  // Computed
  const connectionStatus = computed(() => {
    if (isConnected.value) return 'connected'
    if (reconnectAttempts.value > 0) return 'reconnecting'
    return 'disconnected'
  })
  
  // Actions
  const connect = () => {
    try {
      const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/ws'
      socket.value = new WebSocket(wsUrl)
      
      socket.value.onopen = () => {
        isConnected.value = true
        reconnectAttempts.value = 0
        reconnectDelay.value = 1000
        console.log('WebSocket connected')
        toast.success('Connected to server')
      }
      
      socket.value.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          lastMessage.value = message
          messageHistory.value.push(message)
          
          // Keep only last 100 messages
          if (messageHistory.value.length > 100) {
            messageHistory.value = messageHistory.value.slice(-100)
          }
          
          handleMessage(message)
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }
      
      socket.value.onclose = () => {
        isConnected.value = false
        console.log('WebSocket disconnected')
        
        if (reconnectAttempts.value < maxReconnectAttempts) {
          setTimeout(() => {
            reconnectAttempts.value++
            reconnectDelay.value = Math.min(reconnectDelay.value * 2, 30000) // Max 30 seconds
            console.log(`Reconnecting... Attempt ${reconnectAttempts.value}`)
            connect()
          }, reconnectDelay.value)
        } else {
          toast.error('Connection lost. Please refresh the page.')
        }
      }
      
      socket.value.onerror = (error) => {
        console.error('WebSocket error:', error)
        toast.error('Connection error occurred')
      }
      
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error)
      toast.error('Failed to connect to server')
    }
  }
  
  const disconnect = () => {
    if (socket.value) {
      socket.value.close()
      socket.value = null
    }
    isConnected.value = false
    reconnectAttempts.value = maxReconnectAttempts // Prevent reconnection
  }
  
  const sendMessage = (message: any) => {
    if (socket.value && isConnected.value) {
      socket.value.send(JSON.stringify(message))
    } else {
      console.warn('WebSocket not connected, cannot send message')
      toast.warning('Not connected to server')
    }
  }
  
  const handleMessage = (message: WebSocketMessage) => {
    switch (message.type) {
      case 'connection_established':
        console.log('Connection established:', message.data)
        break
        
      case 'price_update':
        // Handle price updates - will be processed by market store
        break
        
      case 'alert':
        // Handle alert notifications
        if (message.data) {
          toast.warning(`Alert: ${message.data.message || 'New alert triggered'}`)
        }
        break
        
      case 'trading_signal':
        // Handle trading signals
        if (message.data) {
          const signal = message.data
          toast.info(`Trading Signal: ${signal.signal_type?.toUpperCase()} ${signal.symbol} at $${signal.trigger_price}`)
        }
        break
        
      case 'heartbeat':
        // Handle heartbeat - just log for debugging
        console.debug('Heartbeat received')
        break
        
      case 'error':
        console.error('Server error:', message.data)
        toast.error(message.data?.message || 'Server error occurred')
        break
        
      default:
        console.log('Unknown message type:', message.type, message.data)
    }
  }
  
  const subscribeToTicker = (symbol: string) => {
    sendMessage({
      type: 'subscribe',
      data: {
        action: 'subscribe_ticker',
        symbol: symbol
      }
    })
  }
  
  const unsubscribeFromTicker = (symbol: string) => {
    sendMessage({
      type: 'unsubscribe',
      data: {
        action: 'unsubscribe_ticker',
        symbol: symbol
      }
    })
  }
  
  const clearMessageHistory = () => {
    messageHistory.value = []
  }
  
  return {
    // State
    isConnected,
    reconnectAttempts,
    lastMessage,
    messageHistory,
    
    // Computed
    connectionStatus,
    
    // Actions
    connect,
    disconnect,
    sendMessage,
    subscribeToTicker,
    unsubscribeFromTicker,
    clearMessageHistory
  }
})
